import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { format } from "d3-format";
import {
  discontinuousTimeScaleProviderBuilder,
  Chart,
  ChartCanvas,
  BarSeries,
  CandlestickSeries,
  lastVisibleItemBasedZoomAnchor,
  mouseBasedZoomAnchor,
  XAxis,
  YAxis,
  CrossHairCursor,
  EdgeIndicator,
  MouseCoordinateX,
  MouseCoordinateY,
  ZoomButtons,
  withDeviceRatio,
  withSize,
  GenericChartComponent,
} from "react-financial-charts";
import { useMarketData } from "../data/useMarketData";
import OHLCTooltip from "../data/OHLCTooltip";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
const ET = "America/New_York";
dayjs.tz.setDefault(ET);

// ---------- d3-like pattern → Day.js (for crosshair/tooltip) ----------
const D3_TO_DAYJS = {
  "%Y": "YYYY",
  "%y": "YY",
  "%m": "MM",
  "%-m": "M",
  "%d": "DD",
  "%-d": "D",
  "%B": "MMMM",
  "%b": "MMM",
  "%H": "HH",
  "%-H": "H",
  "%I": "hh",
  "%-I": "h",
  "%M": "mm",
  "%-M": "m",
  "%S": "ss",
  "%-S": "s",
  "%p": "A",
};
function d3ToDayjsPattern(d3Fmt) {
  return d3Fmt.replace(
    /%Y|%y|%B|%b|%m|%-m|%d|%-d|%H|%-H|%I|%-I|%M|%-M|%S|%-S|%p/g,
    (m) => D3_TO_DAYJS[m] ?? m
  );
}
function makeEtFormatter(d3Pattern) {
  const p = d3ToDayjsPattern(d3Pattern);
  return (d) => dayjs(d).tz(ET).format(p);
}
// ---------------------------------------------------------------------

const axisStyles = {
  strokeStyle: "#383E55",
  strokeWidth: 2,
  tickLabelFill: "#9EAAC7",
  tickStrokeStyle: "#383E55",
  gridLinesStrokeStyle: "rgba(56, 62, 85, 0.5)",
};

const coordinateStyles = {
  fill: "#383E55",
  textFill: "#FFFFFF",
};

const zoomButtonStyles = {
  fill: "#383E55",
  fillOpacity: 0.75,
  strokeWidth: 0,
  textFill: "#9EAAC7",
};

const crossHairStyles = {
  strokeStyle: "#9EAAC7",
};

const openCloseColor = (d) => (d.close > d.open ? "#26a69a" : "#ef5350");

const yExtentsCalculator = ({ plotData }) => {
  let min;
  let max;
  for (const { low, high } of plotData) {
    if (min === undefined) min = low;
    if (max === undefined) max = high;
    if (low !== undefined && min > low) min = low;
    if (high !== undefined && max < high) max = high;
  }
  if (min === undefined) min = 0;
  if (max === undefined) max = 0;

  const padding = (max - min) * 0.1;
  return [min - padding, max + padding * 2];
};

/**
 * SessionHighlight (ALWAYS-VISIBLE EXTENDED HOURS)
 */
function SessionHighlight({
  dayEntries, // [ [dayKey, rows], ... ]
  rthStart = "09:29",
  rthEnd = "16:01",
  fill = "rgba(100, 149, 237, 0.10)",
}) {
  const canvasDraw = (ctx, moreProps) => {
    const {
      xScale,
      chartConfig: { height, origin },
      xAccessor,
    } = moreProps;

    const [, oy] = origin;
    const yTop = oy;
    const yBottom = oy + height;

    const paint = (x0, x1) => {
      const left = Math.min(x0, x1);
      const right = Math.max(x0, x1);
      const w = Math.max(1, right - left);
      ctx.fillRect(left, yTop, w, yBottom - yTop);
    };

    ctx.save();
    ctx.fillStyle = fill;

    for (let i = 0; i < dayEntries.length; i++) {
      const [key, rows] = dayEntries[i];
      if (!rows || rows.length === 0) continue;

      const first = rows[0];
      const last = rows[rows.length - 1];
      const xFirst = xScale(xAccessor(first));
      const xLast = xScale(xAccessor(last));

      const prevRows = i > 0 ? dayEntries[i - 1][1] : null;
      const nextRows = i < dayEntries.length - 1 ? dayEntries[i + 1][1] : null;
      const prevLastX = prevRows
        ? xScale(xAccessor(prevRows[prevRows.length - 1]))
        : xFirst - 12;
      const nextFirstX = nextRows ? xScale(xAccessor(nextRows[0])) : xLast + 12;
      const midLeft = (prevLastX + xFirst) / 2;
      const midRight = (xLast + nextFirstX) / 2;

      const start = dayjs
        .tz(`${key} ${rthStart}`, "YYYY-MM-DD HH:mm", ET)
        .toDate();
      const end = dayjs.tz(`${key} ${rthEnd}`, "YYYY-MM-DD HH:mm", ET).toDate();

      const inRTH = rows.filter((r) => r.dateET >= start && r.dateET <= end);

      if (inRTH.length === 0) {
        paint(midLeft, midRight);
        continue;
      }

      const xFirstRTH = xScale(xAccessor(inRTH[0]));
      const xLastRTH = xScale(xAccessor(inRTH[inRTH.length - 1]));
      if (xFirstRTH > midLeft) paint(midLeft, xFirstRTH);
      if (midRight > xLastRTH) paint(xLastRTH, midRight);
    }

    ctx.restore();
  };

  return (
    <GenericChartComponent
      canvasDraw={canvasDraw}
      drawOn={["pan", "zoom", "drag", "mousemove", "draw"]}
    />
  );
}

/**
 * TradeMarkers — place arrows relative to candle high/low
 */
function TradeMarkers({ points }) {
  const canvasDraw = (ctx, moreProps) => {
    const {
      xScale,
      xAccessor,
      chartConfig: { yScale }, // ✅ get yScale from chartConfig
    } = moreProps;

    const pad = 6;

    const drawTriangle = (x, y, up, color) => {
      ctx.beginPath();
      if (up) {
        // up triangle (buy) below candle
        ctx.moveTo(x, y - 6);
        ctx.lineTo(x - 5, y + 4);
        ctx.lineTo(x + 5, y + 4);
      } else {
        // down triangle (sell) above candle
        ctx.moveTo(x, y + 6);
        ctx.lineTo(x - 5, y - 4);
        ctx.lineTo(x + 5, y - 4);
      }
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    };

    ctx.save();
    for (const p of points) {
      const { datum, side } = p;
      const x = xScale(xAccessor(datum));
      if (!Number.isFinite(x)) continue;

      if (side === "buy") {
        const y = yScale(datum.low) + pad;
        drawTriangle(x, y, true, "#26a69a");
      } else {
        const y = yScale(datum.high) - pad;
        drawTriangle(x, y, false, "#ef5350");
      }
    }
    ctx.restore();
  };

  return (
    <GenericChartComponent
      canvasDraw={canvasDraw}
      drawOn={["pan", "zoom", "drag", "mousemove", "draw"]}
    />
  );
}

const FinancialChart = ({
  dateTimeFormat,
  height,
  margin,
  priceDisplayFormat,
  ratio,
  width,
  trades,
}) => {
  const [resetCount, setResetCount] = useState(0);
  const { data: initialData, loaded } = useMarketData(null, null, trades);

  const timeDisplayFormatET = useMemo(
    () => makeEtFormatter(dateTimeFormat),
    [dateTimeFormat]
  );

  // 1) Precompute ET fields once
  const dataET = useMemo(() => {
    if (!initialData) return [];
    return initialData.map((d) => {
      const ts = dayjs.tz(d.date, ET).valueOf();
      return {
        ...d,
        dateET: new Date(ts),
        tsET: ts,
        dayKeyET: dayjs(ts).tz(ET).format("YYYY-MM-DD"),
      };
    });
  }, [initialData]);

  // 2) Feed ET to the scale
  const xScaleProvider = useMemo(
    () =>
      discontinuousTimeScaleProviderBuilder().inputDateAccessor(
        (d) => d.dateET
      ),
    []
  );
  const { data, xScale, xAccessor, displayXAccessor } = useMemo(
    () => xScaleProvider(dataET),
    [xScaleProvider, dataET]
  );

  // 3) Layout helpers + default extents (fallback)
  const approxWindow = useMemo(
    () => Math.max(0, data.length - parseInt((width || 0) / 5 || 0, 10)),
    [data.length, width]
  );
  const fallbackMin = data.length
    ? xAccessor(data[Math.max(0, approxWindow)])
    : 0;
  const fallbackMax = data.length ? xAccessor(data[data.length - 1]) : 1;

  // 4) Group by day once and create ordered entries for highlight
  const groupsByDay = useMemo(() => {
    const m = new Map();
    for (const row of data) {
      const k = row.dayKeyET;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(row);
    }
    return m;
  }, [data]);

  // 5) Axis tick formatting
  const isSingleDay = useMemo(() => {
    if (!data.length) return true;
    const firstIdx = Math.max(0, approxWindow);
    const lastIdx = Math.max(0, data.length - 1);
    const firstDateET = dayjs(displayXAccessor(data[firstIdx])).tz(ET);
    const lastDateET = dayjs(displayXAccessor(data[lastIdx])).tz(ET);
    return firstDateET.isSame(lastDateET, "day");
  }, [data, approxWindow, displayXAccessor]);

  const hourFmt = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: ET,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    []
  );
  const dayHourFmt = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: ET,
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    []
  );

  const xTickFormat = useCallback(
    (tick) => {
      if (!data.length) return "";
      const inv = xScale.invert ? xScale.invert(tick) : tick;
      const date =
        inv && typeof inv === "object" && "date" in inv
          ? displayXAccessor(inv)
          : displayXAccessor(
              data[Math.max(0, Math.min(data.length - 1, Math.round(inv)))]
            );
      return isSingleDay ? hourFmt.format(date) : dayHourFmt.format(date);
    },
    [xScale, displayXAccessor, data, isSingleDay, hourFmt, dayHourFmt]
  );

  // 6) Trade markers (match to nearest candle, keep datum)
  const points = useMemo(() => {
    const out = [];
    const singles = trades?.singleTrades || [];
    if (!data.length || !singles.length) return out;

    const times = data.map((d) => displayXAccessor(d).getTime());

    const nearestIndex = (tMs) => {
      let lo = 0,
        hi = times.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (times[mid] < tMs) lo = mid + 1;
        else hi = mid;
      }
      if (lo > 0 && Math.abs(times[lo] - tMs) >= Math.abs(times[lo - 1] - tMs))
        return lo - 1;
      return lo;
    };

    for (const tr of singles) {
      const raw = String(tr.dateTime); // e.g. 20250808152117
      if (!/^\d{14}$/.test(raw)) continue;
      const etDate = dayjs.tz(raw, "YYYYMMDDHHmmss", ET).toDate();
      const idx = nearestIndex(etDate.getTime());
      const datum = data[idx];
      if (!datum) continue;

      out.push({
        datum,
        side: tr.transaction === "buy" ? "buy" : "sell",
      });
    }
    return out;
  }, [trades, data, displayXAccessor]);

  // 7) INITIAL VIEWPORT: show the first trade's ET day (once, and on Reset)
  const [xExtents, setXExtents] = useState(null);

  const computeExtentsForDate = useCallback(
    (targetDate) => {
      if (!data.length || !targetDate) {
        return [fallbackMin, fallbackMax + 1];
      }
      const times = data.map((d) => displayXAccessor(d).getTime());

      const nearestIndex = (tMs) => {
        let lo = 0,
          hi = times.length - 1;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (times[mid] < tMs) lo = mid + 1;
          else hi = mid;
        }
        if (
          lo > 0 &&
          Math.abs(times[lo] - tMs) >= Math.abs(times[lo - 1] - tMs)
        )
          return lo - 1;
        return lo;
      };

      const start = dayjs(targetDate).tz(ET).startOf("day").toDate().getTime();
      const end = dayjs(targetDate).tz(ET).endOf("day").toDate().getTime();

      const iStart = Math.max(
        0,
        Math.min(times.length - 1, nearestIndex(start))
      );
      const iEnd = Math.max(0, Math.min(times.length - 1, nearestIndex(end)));

      const minAcc = xAccessor(data[Math.min(iStart, iEnd)]);
      const maxAcc = xAccessor(data[Math.max(iStart, iEnd)]);

      return [minAcc, maxAcc + 1];
    },
    [data, displayXAccessor, xAccessor, fallbackMin, fallbackMax]
  );

  const firstTradeDate = useMemo(() => {
    const singles = trades?.singleTrades || [];
    if (!singles.length) return null;
    const first = singles.reduce(
      (min, t) => (!min || t.dateTime < min ? t.dateTime : min),
      null
    );
    if (!first || !/^\d{14}$/.test(String(first))) return null;
    return dayjs.tz(String(first), "YYYYMMDDHHmmss", ET).toDate();
  }, [trades]);

  // initialize once when data is ready, and recompute on Reset
  useEffect(() => {
    if (!data.length) return;
    const initial = computeExtentsForDate(firstTradeDate);
    setXExtents(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, resetCount]); // reset goes back to first trade day

  const dayEntries = useMemo(() => {
    const entries = Array.from(groupsByDay.entries());
    entries.sort((a, b) => (a[0] < b[0] ? -1 : 1));
    return entries;
  }, [groupsByDay]);

  // render guard after all hooks ran
  if (!loaded || !height || !ratio || !width || !data.length || !xExtents)
    return null;

  return (
    <ChartCanvas
      height={height}
      ratio={ratio}
      width={width}
      margin={margin}
      seriesName={`Chart ${resetCount}`}
      data={data}
      xScale={xScale}
      xAccessor={xAccessor}
      displayXAccessor={displayXAccessor}
      xExtents={xExtents}
      zoomAnchor={mouseBasedZoomAnchor}
    >
      {/* Volume (bottom) */}
      <Chart
        id={1}
        height={Math.max(
          0,
          (height - (margin?.top || 0) - (margin?.bottom || 0)) / 5
        )}
        origin={(_, h) => [
          0,
          h -
            Math.max(
              0,
              (height - (margin?.top || 0) - (margin?.bottom || 0)) / 5
            ),
        ]}
        yExtents={(d) => d.volume}
      >
        <BarSeries
          fillStyle={(d) =>
            d.close > d.open
              ? "rgba(38, 166, 154, 0.3)"
              : "rgba(239, 83, 80, 0.3)"
          }
          yAccessor={(d) => d.volume}
        />
      </Chart>

      {/* Price (top) */}
      <Chart id={2} yExtentsCalculator={yExtentsCalculator}>
        {/* Extended-hours highlight */}
        <SessionHighlight
          dayEntries={dayEntries}
          rthStart="09:29"
          rthEnd="16:01"
          fill="rgba(255, 215, 0, 0.08)"
        />

        {/* Trade markers relative to candles */}
        <TradeMarkers points={points} />

        <XAxis
          {...axisStyles}
          showGridLines
          tickFormat={xTickFormat}
          ticks={6}
        />
        <MouseCoordinateX
          displayFormat={timeDisplayFormatET}
          {...coordinateStyles}
        />
        <YAxis {...axisStyles} showGridLines />
        <MouseCoordinateY
          rectWidth={margin.right}
          displayFormat={priceDisplayFormat}
          {...coordinateStyles}
        />

        <EdgeIndicator
          itemType="last"
          rectWidth={margin.right}
          fill={openCloseColor}
          lineStroke={openCloseColor}
          displayFormat={priceDisplayFormat}
          yAccessor={(d) => d.close}
        />

        <CandlestickSeries />
        <OHLCTooltip
          origin={[8, 16]}
          textFill={openCloseColor}
          className="react-no-select"
        />
        <ZoomButtons
          onReset={() => setResetCount((c) => c + 1)}
          {...zoomButtonStyles}
        />
      </Chart>

      <CrossHairCursor {...crossHairStyles} />
    </ChartCanvas>
  );
};

FinancialChart.propTypes = {
  dateTimeFormat: PropTypes.string,
  height: PropTypes.number,
  margin: PropTypes.object,
  priceDisplayFormat: PropTypes.func,
  ratio: PropTypes.number,
  width: PropTypes.number,
};

FinancialChart.defaultProps = {
  dateTimeFormat: "%d %b '%y \xa0 %H:%M",
  height: 0,
  margin: { left: 0, right: 48, top: 0, bottom: 24 },
  priceDisplayFormat: format(".2f"),
  ratio: 0,
  width: 0,
};

export const CandleStickChart = withSize({ style: { minHeight: 600 } })(
  withDeviceRatio()(FinancialChart)
);
